import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, LayersClear } from '@mui/icons-material';
import { Formik, FormikHelpers } from 'formik';
import { object, string } from 'yup';
import { getDocs, query, where } from 'firebase/firestore';
import {
  bulkClearLabelUrlsForEvent,
  createPrinter,
  deletePrinter,
  updatePrinter,
  useActor,
} from '../../lib/audited-writes';
import { attendeesCollectionRef } from '../../lib/firebase';
import { hasCachedLabel } from '../../lib/writes/label-cache';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { AppEvent, Printer } from '../../lib/types';
import { NO_PRINT_ID } from '../../lib/constants';

const PRINTER_NAME_MAX_LENGTH = 100;

// Synthetic ref used by FirebaseProvider for the "No printer" sentinel.
// We exclude it from this management UI since it's not a real Firestore doc.
const SYNTHETIC_NOPRINT_REF = 'noprint';

type PrinterFormValues = {
  name: string;
  // String in the form (TextField type='number' stores its raw string value),
  // parsed to a number on submit.
  id: string;
};

const blankPrinterValues: PrinterFormValues = {
  name: '',
  id: '',
};

const buildPrinterSchema = (existingPrinters: Printer[], excludeRef?: string) =>
  object().shape({
    name: string()
      .trim()
      .required('Name is required')
      .max(
        PRINTER_NAME_MAX_LENGTH,
        `Name must be ${PRINTER_NAME_MAX_LENGTH} characters or less`
      ),
    id: string()
      .trim()
      .required('ID is required')
      .matches(
        /^(?:0|[1-9]\d*)$/,
        'ID must be a non-negative whole number (no leading zeros)'
      )
      .test(
        'not-reserved',
        `ID ${NO_PRINT_ID} is reserved`,
        (v) => Number(v) !== NO_PRINT_ID
      )
      .test(
        'unique',
        'A printer with this ID already exists',
        function (v) {
          if (!v) return true;
          const id = Number(v);
          const conflict = existingPrinters.find(
            (p) => p.ref !== excludeRef && p.id === id
          );
          return !conflict;
        }
      ),
  });

const PrinterFormDialog = ({
  open,
  printer,
  printers,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  printer: Printer | null;
  printers: Printer[];
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const isEdit = printer !== null;
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const initialValues = useMemo<PrinterFormValues>(
    () =>
      printer
        ? { name: printer.name, id: String(printer.id) }
        : blankPrinterValues,
    [printer]
  );

  const validationSchema = useMemo(
    () => buildPrinterSchema(printers, printer?.ref),
    [printers, printer?.ref]
  );

  const handleSubmit = async (
    values: PrinterFormValues,
    { setSubmitting }: FormikHelpers<PrinterFormValues>
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const payload = {
        name: values.name.trim(),
        id: Number(values.id),
      };
      if (printer) {
        await updatePrinter(actor, printer, payload);
      } else {
        await createPrinter(actor, payload);
      }
      succeeded = true;
    } catch (error) {
      console.error('Error saving printer', error);
      onError(isEdit ? 'Failed to update printer' : 'Failed to add printer');
    } finally {
      setSubmitting(false);
      setIsWorking(false);
    }
    if (succeeded) {
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? 'Edit printer' : 'Add printer'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                <TextField
                  name='name'
                  label='Printer name'
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={
                    (touched.name && errors.name) ||
                    `${values.name.length}/${PRINTER_NAME_MAX_LENGTH}`
                  }
                  inputProps={{ maxLength: PRINTER_NAME_MAX_LENGTH }}
                  disabled={isSubmitting}
                  fullWidth
                />
                <TextField
                  name='id'
                  label='Printer ID'
                  type='number'
                  value={values.id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.id && Boolean(errors.id)}
                  helperText={touched.id && errors.id}
                  disabled={isSubmitting}
                  fullWidth
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting}
              >
                {isEdit ? 'Save' : 'Add printer'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const DeletePrinterDialog = ({
  printer,
  onClose,
  onDeleted,
  onError,
}: {
  printer: Printer | null;
  onClose: () => void;
  onDeleted: () => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!printer) return;
    setIsWorking(true);
    try {
      await deletePrinter(actor, printer);
      setIsWorking(false);
      onDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting printer', error);
      onError('Failed to delete printer');
      setIsWorking(false);
    }
  };

  return (
    <Dialog
      open={Boolean(printer)}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Delete printer</DialogTitle>
      <DialogContent>
        {printer && (
          <DialogContentText>
            Delete <strong>{printer.name}</strong> (ID {printer.id})?
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color='error'
          variant='contained'
          disabled={isWorking}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Counts and clears cached `s3LabelUrl` values for every attendee in the
// given event (including soft-deleted ones — if they get restored later,
// no stale cache survives). The count shown to the user reflects only docs
// that actually have a cached URL, so the confirm step is honest about what
// will change. Closing the dialog while the read is in flight cancels the
// state update with a local `cancelled` flag to avoid setting state on an
// unmounted component.
const ClearLabelCacheDialog = ({
  event,
  onClose,
  onDone,
  onError,
}: {
  event: AppEvent | null;
  onClose: () => void;
  onDone: (cleared: number, failed: number, eventName: string) => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const [refs, setRefs] = useState<string[] | null>(null);
  const [countError, setCountError] = useState(false);
  const [isWorking, setIsWorking] = useState(false);

  useEffect(() => {
    if (!event) {
      setRefs(null);
      setCountError(false);
      return;
    }
    setRefs(null);
    setCountError(false);
    let cancelled = false;
    getDocs(query(attendeesCollectionRef, where('eventRef', '==', event.ref)))
      .then((snap) => {
        if (cancelled) return;
        const ids = snap.docs
          .filter((d) =>
            hasCachedLabel((d.data() as { s3LabelUrl?: unknown }).s3LabelUrl),
          )
          .map((d) => d.id);
        setRefs(ids);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to read attendees for label-cache clear', err);
        setCountError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [event]);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!event || refs === null) return;
    setIsWorking(true);
    try {
      const { cleared, failed } = await bulkClearLabelUrlsForEvent(
        actor,
        event,
        refs,
      );
      onDone(cleared, failed, event.name);
      onClose();
    } catch (error) {
      console.error('Error clearing label cache', error);
      onError('Failed to clear cached labels');
    } finally {
      setIsWorking(false);
    }
  };

  const count = refs?.length ?? 0;

  return (
    <Dialog open={Boolean(event)} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>Clear cached labels</DialogTitle>
      <DialogContent>
        {event && (
          <Stack spacing={2}>
            <Alert severity='info'>
              Clears the cached printable-label PDF URL for every attendee in{' '}
              <strong>{event.name}</strong>. No attendee data is affected — the
              next print regenerates each label from current data.
            </Alert>

            <DialogContentText>
              {refs === null && !countError && (
                <Box
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
                >
                  <CircularProgress size={14} />
                  Counting cached labels…
                </Box>
              )}
              {countError && (
                <Typography color='warning.main' component='span'>
                  Could not load attendees. Try again.
                </Typography>
              )}
              {refs !== null && count > 0 && (
                <>
                  <strong>{count}</strong> attendee{count === 1 ? '' : 's'} have
                  a cached label that will be cleared.
                </>
              )}
              {refs !== null && count === 0 && (
                <>No attendees in this event have a cached label right now.</>
              )}
            </DialogContentText>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isWorking}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant='contained'
          disabled={refs === null || count === 0 || isWorking || countError}
          startIcon={
            isWorking ? <CircularProgress size={16} /> : <LayersClear />
          }
        >
          {isWorking ? 'Clearing…' : 'Clear cached labels'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManagePrinters = () => {
  const { printers, activeEvent } = useFirebaseContext();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Printer | null>(null);
  const [deleting, setDeleting] = useState<Printer | null>(null);
  const [clearingLabels, setClearingLabels] = useState(false);

  // Make sure the dialog doesn't silently re-open with a new event if the
  // active event flips while it was open. Without this, the dialog would
  // close (via `open={Boolean(event)}` going false) but `clearingLabels`
  // would stay `true`, so a subsequent re-activation would pop it back up
  // with stale intent.
  useEffect(() => {
    if (!activeEvent) setClearingLabels(false);
  }, [activeEvent]);

  const realPrinters = useMemo(
    () => printers.filter((p) => p.ref !== SYNTHETIC_NOPRINT_REF),
    [printers]
  );

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setAdding(true)}
        >
          Add printer
        </Button>
      </Box>
      {realPrinters.length === 0 ? (
        <Typography color='text.secondary' textAlign='center'>
          No printers yet. Click <strong>Add printer</strong> to add one.
        </Typography>
      ) : (
        <List dense disablePadding>
          {realPrinters.map((printer) => (
            <ListItem
              key={printer.ref}
              divider
              sx={{
                py: 0.25,
                px: 1,
                gap: 1,
                '&:last-child': { borderBottom: 0 },
              }}
              secondaryAction={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    edge='end'
                    size='small'
                    aria-label={`Edit ${printer.name}`}
                    onClick={() => setEditing(printer)}
                  >
                    <Edit fontSize='small' />
                  </IconButton>
                  <IconButton
                    edge='end'
                    size='small'
                    aria-label={`Delete ${printer.name}`}
                    onClick={() => setDeleting(printer)}
                  >
                    <Delete fontSize='small' />
                  </IconButton>
                </Box>
              }
            >
              <Typography
                variant='body2'
                sx={{ minWidth: 200, flexShrink: 0, fontWeight: 500 }}
                noWrap
              >
                {printer.name}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ flexGrow: 1 }}
              >
                ID: {printer.id}
              </Typography>
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 3 }} />
      <Box>
        <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
          Label cache
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          Force-regenerate every printed label for the active event. The cached
          label PDF URL is cleared on each attendee; the next print rebuilds
          the label from current data. Useful after a label-template change or
          when something looks off on printed badges.
        </Typography>
        <Button
          variant='outlined'
          startIcon={<LayersClear />}
          onClick={() => setClearingLabels(true)}
          disabled={!activeEvent}
        >
          Clear cached labels for active event
        </Button>
        {!activeEvent && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ ml: 1.5 }}
          >
            No active event.
          </Typography>
        )}
      </Box>

      <PrinterFormDialog
        open={adding || editing !== null}
        printer={editing}
        printers={realPrinters}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={(mode) =>
          toast.success(mode === 'add' ? 'Printer added' : 'Printer saved')
        }
        onError={(msg) => toast.error(msg)}
      />

      <DeletePrinterDialog
        printer={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={() => toast.success('Printer deleted')}
        onError={(msg) => toast.error(msg)}
      />

      <ClearLabelCacheDialog
        event={clearingLabels ? activeEvent ?? null : null}
        onClose={() => setClearingLabels(false)}
        onDone={(cleared, failed, name) => {
          if (failed > 0 && cleared === 0) {
            toast.error(
              `Failed to clear cached labels in "${name}". Try again.`,
            );
            return;
          }
          if (failed > 0) {
            toast.warning(
              `Cleared ${cleared} cached label${cleared === 1 ? '' : 's'} in "${name}", but ${failed} failed. Try again to retry the rest.`,
            );
            return;
          }
          toast.success(
            cleared === 0
              ? `No cached labels to clear in "${name}"`
              : `Cleared cached labels for ${cleared} attendee${cleared === 1 ? '' : 's'} in "${name}"`,
          );
        }}
        onError={(msg) => toast.error(msg)}
      />
    </>
  );
};

export default ManagePrinters;
